/**
 * EmojiPicker Component
 * Reusable emoji picker with categorized emojis
 */

import { useState, useRef, useEffect } from 'react';
import { Smile, X } from 'lucide-react';
import { cn } from '@/utils/classNames';

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface EmojiCategory {
  name: string;
  icon: string;
  emojis: string[];
}

const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    name: 'Smileys',
    icon: '😊',
    emojis: [
      '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
      '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘',
      '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪',
      '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒',
    ],
  },
  {
    name: 'Animals',
    icon: '🐶',
    emojis: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼',
      '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔',
      '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺',
      '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞',
    ],
  },
  {
    name: 'Objects',
    icon: '⚽',
    emojis: [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉',
      '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍',
      '🏏', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋',
      '🎽', '🛹', '🛼', '🛷', '⛸️', '🥌', '🎿', '⛷️',
    ],
  },
  {
    name: 'Technology',
    icon: '💻',
    emojis: [
      '💻', '🖥️', '🖨️', '⌨️', '🖱️', '🖲️', '💽', '💾',
      '💿', '📀', '📱', '📲', '☎️', '📞', '📟', '📠',
      '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️',
      '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡',
    ],
  },
  {
    name: 'Travel',
    icon: '✈️',
    emojis: [
      '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑',
      '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🦯', '🦽',
      '🦼', '🛴', '🚲', '🛵', '🏍️', '🛺', '🚨', '🚔',
      '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋',
      '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇',
      '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️', '💺', '🛰️',
    ],
  },
  {
    name: 'Food',
    icon: '🍕',
    emojis: [
      '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐',
      '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅',
      '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽',
      '🥕', '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯',
      '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞',
      '🧇', '🥓', '🥩', '🍗', '🍖', '🌭', '🍔', '🍟',
      '🍕', '🫓', '🥪', '🥙', '🧆', '🌮', '🌯', '🫔',
    ],
  },
  {
    name: 'Symbols',
    icon: '⭐',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖',
      '💘', '💝', '⭐', '🌟', '✨', '💫', '⚡', '🔥',
      '💥', '💢', '💦', '💨', '🕊️', '🦋', '🌈', '☀️',
      '🌙', '⭐', '🌠', '🌌', '☁️', '⛅', '⛈️', '🌤️',
      '✅', '❌', '⭕', '🚫', '💯', '🔰', '⚠️', '🚸',
    ],
  },
  {
    name: 'Work',
    icon: '💼',
    emojis: [
      '💼', '📊', '📈', '📉', '📋', '📝', '📄', '📃',
      '📑', '📁', '📂', '🗂️', '📅', '📆', '🗓️', '📇',
      '🗃️', '🗳️', '🗄️', '📦', '📪', '📫', '📬', '📭',
      '📮', '📯', '📜', '📃', '📄', '📑', '🔖', '🏷️',
      '💰', '💴', '💵', '💶', '💷', '💸', '💳', '🧾',
    ],
  },
];

export function EmojiPicker({
  value,
  onChange,
  placeholder = 'Select emoji',
  className = '',
  disabled = false,
}: EmojiPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleEmojiSelect = (emoji: string) => {
    onChange(emoji);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  // Filter emojis based on search query
  const filteredEmojis = searchQuery
    ? EMOJI_CATEGORIES.flatMap(cat => cat.emojis).filter(emoji =>
        emoji.includes(searchQuery)
      )
    : EMOJI_CATEGORIES[selectedCategory].emojis;

  return (
    <div ref={pickerRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex items-center justify-center gap-2',
          'w-full rounded-lg border-2 border-border-medium bg-bg-primary',
          'px-4 py-2.5 text-left transition-colors',
          'hover:border-stellar-primary hover:bg-bg-secondary',
          'focus:border-stellar-primary focus:outline-none',
          'focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {value ? (
          <>
            <span className="text-2xl">{value}</span>
            <span className="flex-1 text-sm text-text-secondary">
              {value}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="rounded p-1 hover:bg-bg-tertiary"
            >
              <X className="h-3 w-3 text-text-secondary" />
            </button>
          </>
        ) : (
          <>
            <Smile className="h-5 w-5 text-text-tertiary" />
            <span className="flex-1 text-sm text-text-secondary">{placeholder}</span>
          </>
        )}
      </button>

      {/* Emoji Picker Dropdown */}
      {isOpen && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-2',
            'w-80 rounded-xl border-2 border-border-default',
            'bg-bg-primary shadow-2xl'
          )}
        >
          {/* Search */}
          <div className="border-b border-border-default p-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search emoji..."
              className={cn(
                'w-full rounded-lg border border-border-medium',
                'px-3 py-2 text-sm',
                'focus:border-stellar-primary focus:outline-none',
                'focus:ring-2 focus:ring-stellar-primary',
                'focus:ring-opacity-20'
              )}
            />
          </div>

          {/* Category Tabs */}
          {!searchQuery && (
            <div className="flex gap-1 border-b border-border-default px-2 py-2">
              {EMOJI_CATEGORIES.map((category, index) => (
                <button
                  key={category.name}
                  type="button"
                  onClick={() => setSelectedCategory(index)}
                  className={cn(
                    'rounded-lg p-2 text-xl transition-colors',
                    selectedCategory === index
                      ? 'bg-stellar-primary-light text-stellar-primary'
                      : 'text-text-secondary hover:bg-bg-secondary'
                  )}
                  title={category.name}
                >
                  {category.icon}
                </button>
              ))}
            </div>
          )}

          {/* Emoji Grid */}
          <div className="max-h-64 overflow-y-auto p-3">
            <div className="grid grid-cols-8 gap-2">
              {filteredEmojis.map((emoji, index) => (
                <button
                  key={`${emoji}-${index}`}
                  type="button"
                  onClick={() => handleEmojiSelect(emoji)}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center',
                    'rounded-lg text-2xl transition-colors',
                    'hover:bg-stellar-primary-light',
                    'focus:outline-none focus:ring-2',
                    'focus:ring-stellar-primary'
                  )}
                  title={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {filteredEmojis.length === 0 && (
              <div className="py-8 text-center text-sm text-text-secondary">
                No emojis found
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border-default px-3 py-2 text-center">
            <p className="text-xs text-text-secondary">
              Click an emoji to select it
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
